<template>
    <div class="task-status-bar">
        <div class="status-stages">
            <div
                v-for="(stage, index) in statusList"
                :key="stage._id"
                :class="[
                    'status-stage',
                    {
                        active: index <= currentStatusIndex,
                        current: index === currentStatusIndex,
                        completed: index < currentStatusIndex,
                    },
                ]"
                @click="updateStatus(index)"
                :style="getStageStyle(index)"
            >
                <div class="stage-circle">
                    <span 
                        v-if="index < currentStatusIndex" 
                        class="material-icons check-icon"
                    >
                        check
                    </span>
                    <span v-else class="stage-number">{{ index + 1 }}</span>
                </div>
                
                <div class="stage-line" v-if="index < statusList.length - 1" :style="getLineStyle(index)"></div>
                
                <div class="stage-info">
                    <div class="stage-name">{{ formatStageName(stage.label || stage.title) }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "TaskStatusBar",
    props: {
        currentStatus: {
            type: String,
            required: false,
        },
        statusList: {
            type: Array,
            required: true,
        },
        defaultColor: {
            type: String,
            default: "#91a2b6",
        },
        activeColor: {
            type: String,
            default: "#ec4b1d",
        },
    },
    data() {
        return {};
    },
    computed: {
        currentStatusIndex() {
            const index = this.statusList.findIndex(
                (stage) => stage.title === this.currentStatus || stage.label === this.currentStatus
            );
            return index !== -1 ? index : 0;
        },
    },
    methods: {
        updateStatus(index) {
            const stage = this.statusList[index];
            this.$emit("update-status", stage.title || stage.label);
        },
        getStageStyle(index) {
            const stage = this.statusList[index];
            const isActive = index <= this.currentStatusIndex;

            let color = this.defaultColor;

            if (isActive && stage.color) {
                color = stage.color;
            } else if (isActive) {
                color = this.activeColor;
            }

            return {
                '--stage-color': color,
            };
        },
        getLineStyle(index) {
            const isActive = index < this.currentStatusIndex;
            const stage = this.statusList[index];
            
            let color = '#e0e0e0';
            
            if (isActive && stage.color) {
                color = stage.color;
            } else if (isActive) {
                color = this.activeColor;
            }
            
            return {
                background: color,
            };
        },
        formatStageName(name) {
            return name;
        },
    },
};
</script>

<style scoped lang="scss">
.task-status-bar {
    width: 100%;
    padding: 0;
    margin-bottom: 16px;

    .status-stages {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        width: 100%;
    }

    .status-stage {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        cursor: pointer;
        transition: all 0.25s ease;
        flex: 1;

        &:hover {
            .stage-circle {
                transform: scale(1.1);
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
            }

            .stage-name {
                color: var(--stage-color);
            }
        }

        .stage-circle {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: #fff;
            border: 2px solid #e0e0e0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            font-size: 13px;
            color: #9e9e9e;
            transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
            z-index: 2;
            position: relative;

            .stage-number {
                font-size: 13px;
                line-height: 1;
            }

            .check-icon {
                font-size: 16px;
                color: #fff;
                line-height: 1;
            }
        }

        .stage-line {
            position: absolute;
            top: 16px;
            left: 50%;
            width: 100%;
            height: 2px;
            background: #e0e0e0;
            z-index: 1;
            transition: all 0.3s ease;
        }

        .stage-info {
            margin-top: 10px;
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            padding: 0 8px;

            .stage-name {
                font-size: 12px;
                font-weight: 500;
                color: #757575;
                text-align: center;
                line-height: 1.3;
                transition: all 0.2s ease;
                word-wrap: break-word;
                overflow: hidden;
                text-overflow: ellipsis;
                width: 100%;
            }
        }

        // Active state (tamamlanmış ve aktif olanlar)
        &.active {
            .stage-circle {
                border-color: var(--stage-color);
                background: var(--stage-color);
                color: #fff;
                box-shadow: 0 2px 6px rgba(236, 75, 29, 0.25);
            }

            .stage-name {
                color: #424242;
                font-weight: 600;
            }
        }

        // Current state (şu anki aktif aşama)
        &.current {
            .stage-circle {
                box-shadow: 0 0 0 3px rgba(236, 75, 29, 0.15);
            }

            .stage-name {
                color: var(--stage-color);
                font-weight: 700;
            }
        }

        // Completed state
        &.completed {
            .stage-circle {
                border-color: var(--stage-color);
                background: var(--stage-color);
            }
        }
    }
}

// Daha fazla stage olduğunda otomatik scroll
@media (max-width: 768px) {
    .task-status-bar {
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;

        &::-webkit-scrollbar {
            height: 4px;
        }

        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 2px;
        }

        &::-webkit-scrollbar-thumb {
            background: #ccc;
            border-radius: 2px;

            &:hover {
                background: #999;
            }
        }

        .status-stages {
            min-width: max-content;
        }

        .status-stage {
            .stage-circle {
                width: 28px;
                height: 28px;

                .stage-number {
                    font-size: 12px;
                }

                .check-icon {
                    font-size: 14px;
                }
            }

            .stage-line {
                top: 14px;
                left: 50%;
                width: 100%;
            }

            .stage-info {
                width: 100%;
                padding: 0 4px;

                .stage-name {
                    font-size: 11px;
                }
            }
        }
    }
}
</style>


